import {
  CircleNotchIcon,
  CopyIcon,
  DotsThreeVerticalIcon,
  DownloadSimpleIcon,
  PencilSimpleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useRef } from "react";

import { useResumeThumbnail } from "@/client/services/resume/preview";

export type RecentDocumentKind = "resume" | "cover-letter";

export type RecentDocumentItem = {
  id: string;
  kind: RecentDocumentKind;
  title: string;
  subtitle: string;
  template?: string;
  thumbnailUrl: string;
  updatedAt: string;
};

export type RecentDocumentAction = "edit" | "duplicate" | "delete" | "download";

type RecentDocumentCardProps = {
  document: RecentDocumentItem;
  onOpen: (document: RecentDocumentItem) => void;
  onAction: (document: RecentDocumentItem, action: RecentDocumentAction) => void;
  isDownloading?: boolean;
};

const formatDate = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Date inconnue";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
  }).format(date);
};

export const RecentDocumentCard = ({
  document,
  onOpen,
  onAction,
  isDownloading = false,
}: RecentDocumentCardProps) => {
  const isResume = document.kind === "resume";

  /*
   * Une chaîne vide désactive
   * automatiquement la requête
   * dans useResumePreview.
   *
   * Les lettres de motivation
   * n’appellent donc pas le
   * service d’aperçu des CV.
   */
  const thumbnail = useResumeThumbnail(
    isResume ? document.id : "",
    document.updatedAt,
    document.template,
  );

  const previewUrl = isResume ? thumbnail.src : document.thumbnailUrl;

  const menuRef = useRef<HTMLDetailsElement>(null);

  const closeMenu = () => {
    if (menuRef.current) {
      menuRef.current.open = false;
    }
  };

  const handleAction = (action: RecentDocumentAction) => {
    closeMenu();

    onAction(document, action);
  };

  const formattedDate = formatDate(document.updatedAt);

  return (
    <article
      className={cn(
        "group relative z-0 flex min-w-0 items-center gap-3",
        "overflow-visible rounded-2xl border border-slate-200 bg-white",
        "p-3 pr-20 sm:gap-4 sm:p-3.5 sm:pr-12",
        "shadow-[0_3px_12px_rgba(15,23,42,0.04)]",
        "transition-[transform,border-color,box-shadow] duration-200",
        "hover:-translate-y-0.5 hover:border-indigo-200",
        "hover:shadow-[0_10px_24px_rgba(79,70,229,0.09)]",
        "has-[details[open]]:z-50",
        "has-[details[open]]:translate-y-0",
        "has-[details[open]]:border-indigo-200",
      )}
    >
      <button
        type="button"
        onClick={() => {
          onOpen(document);
        }}
        aria-label={`Ouvrir ${document.title}`}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-3 rounded-xl text-left",
          "focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
          "sm:gap-4",
        )}
      >
        <span
          className={cn(
            "flex h-[88px] w-[68px] shrink-0 items-center justify-center",
            "overflow-hidden rounded-lg border border-slate-200",
            "bg-slate-50 shadow-sm",
            "sm:h-[104px] sm:w-[80px]",
            "lg:h-[92px] lg:w-[70px]",
            "xl:h-[104px] xl:w-[80px]",
          )}
        >
          <img
            src={previewUrl}
            alt=""
            aria-hidden
            decoding="async"
            onError={isResume ? thumbnail.showTemplate : undefined}
            className="h-full w-full object-cover object-top"
          />
        </span>

        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 block text-[13px] font-bold leading-5 text-indigo-950 sm:text-sm">
            {document.title}
          </span>

          <span className="mt-1 block truncate text-[11px] font-medium text-slate-500 sm:text-xs">
            {document.subtitle}
          </span>

          <span className="mt-2 block text-[10px] text-slate-400 sm:text-[11px]">
            Modifié {formattedDate}
          </span>
        </span>
      </button>

      <div className="absolute right-2.5 top-2.5 z-50 flex items-center gap-1.5 sm:right-3 sm:top-3">
        {isResume && (
          <Button
            size="sm"
            disabled={isDownloading}
            title="Télécharger ce document"
            onClick={(event) => {
              event.stopPropagation();

              onAction(document, "download");
            }}
            className="h-8 rounded-lg bg-emerald-500 px-2.5 py-0 text-xs font-semibold text-white shadow-sm transition-transform hover:scale-105 hover:bg-emerald-600 disabled:opacity-70 disabled:hover:scale-100 sm:px-3"
          >
            {isDownloading ? (
              <CircleNotchIcon
                aria-hidden
                size={14}
                weight="bold"
                className="animate-spin sm:mr-1.5"
              />
            ) : (
              <DownloadSimpleIcon aria-hidden size={14} weight="bold" className="sm:mr-1.5" />
            )}

            <span className="hidden sm:inline">
              {isDownloading ? "Génération…" : "Télécharger"}
            </span>
          </Button>
        )}

        <details ref={menuRef} className="relative">
          <summary
            aria-label={`Actions pour ${document.title}`}
            className={cn(
              "grid size-8 cursor-pointer list-none place-items-center",
              "rounded-lg bg-white text-indigo-950",
              "transition-colors hover:bg-indigo-50 hover:text-indigo-700",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-indigo-500",
              "[&::-webkit-details-marker]:hidden",
            )}
          >
            <DotsThreeVerticalIcon aria-hidden size={20} weight="bold" />
          </summary>

          <div
            role="menu"
            aria-label={`Actions pour ${document.title}`}
            className={cn(
              "absolute right-0 top-[38px] z-[60] w-44",
              "overflow-hidden rounded-xl border border-slate-200",
              "bg-white p-1.5",
              "shadow-[0_18px_44px_rgba(15,23,42,0.2)]",
            )}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                handleAction("edit");
              }}
              className={cn(
                "flex min-h-10 w-full items-center gap-2.5 rounded-lg",
                "px-3 text-left text-xs font-semibold text-slate-700",
                "transition-colors hover:bg-indigo-50 hover:text-indigo-700",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-indigo-500",
              )}
            >
              <PencilSimpleIcon aria-hidden size={16} weight="duotone" />
              Modifier
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={() => {
                handleAction("duplicate");
              }}
              className={cn(
                "flex min-h-10 w-full items-center gap-2.5 rounded-lg",
                "px-3 text-left text-xs font-semibold text-slate-700",
                "transition-colors hover:bg-indigo-50 hover:text-indigo-700",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-indigo-500",
              )}
            >
              <CopyIcon aria-hidden size={16} weight="duotone" />
              Dupliquer
            </button>

            <div aria-hidden className="my-1 h-px bg-slate-100" />

            <button
              type="button"
              role="menuitem"
              onClick={() => {
                handleAction("delete");
              }}
              className={cn(
                "flex min-h-10 w-full items-center gap-2.5 rounded-lg",
                "px-3 text-left text-xs font-semibold text-rose-600",
                "transition-colors hover:bg-rose-50 hover:text-rose-700",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-rose-500",
              )}
            >
              <TrashIcon aria-hidden size={16} weight="duotone" />
              Supprimer
            </button>
          </div>
        </details>
      </div>
    </article>
  );
};
